import { css } from 'lit';

export const styles = css`
  :host {
    display: inline-flex;
    box-sizing: border-box;

    /* ── Defaults (matches gray theme + md size + badge variant) ── */
    --asc-badge-padding-x: var(--asc-spacing-md);        /* 8px */
    --asc-badge-padding-y: var(--asc-spacing-xxs);       /* 2px */
    --asc-badge-font-size: var(--asc-font-size-text-sm); /* 14px — md uses text-sm per Figma */
    --asc-badge-line-height: var(--asc-line-height-text-sm); /* 20px */
    --asc-badge-font-weight: var(--asc-font-weight-medium);
    --asc-badge-gap: var(--asc-spacing-xs);              /* 4px */
    --asc-badge-icon-size: var(--asc-spacing-lg);      /* 12px */
    --asc-badge-avatar-size: var(--asc-spacing-xl);      /* 16px */
    --asc-badge-border-radius: var(--asc-radius-xs);     /* 4px – lightly rounded */
    --asc-badge-border-width: var(--asc-border-width);   /* 1px */
    --asc-badge-icon-only-size: var(--asc-spacing-3xl);  /* 24px */
    --asc-badge-bg: var(--asc-bg-primary);
    --asc-badge-border-color: var(--asc-border-primary);
    --asc-badge-fg: var(--asc-text-primary);
    --asc-badge-icon-color: var(--asc-fg-primary);     /* --colors/foreground/fg-primary per Figma */
    --asc-badge-avatar-ring-color: var(--asc-border-primary);
  }

  /* ═══════════════════════════════════════════════════════════
     SIZES
     ═══════════════════════════════════════════════════════════ */
  :host([size='sm']) {
    --asc-badge-padding-x: var(--asc-spacing-sm);         /* 6px */
    --asc-badge-padding-y: var(--asc-spacing-xxs);        /* 2px */
    --asc-badge-font-size: var(--asc-font-size-text-xs);  /* 12px */
    --asc-badge-line-height: var(--asc-line-height-text-xs); /* 18px */
    --asc-badge-gap: var(--asc-spacing-xs);               /* 4px */
    --asc-badge-icon-size: var(--asc-spacing-lg);      /* 12px */
    --asc-badge-avatar-size: var(--asc-spacing-xl);      /* 16px */
    --asc-badge-icon-only-size: var(--asc-spacing-2xl);  /* 20px */
  }

  :host([size='md']) {
    --asc-badge-padding-x: var(--asc-spacing-md);         /* 8px */
    --asc-badge-padding-y: var(--asc-spacing-xxs);        /* 2px */
    --asc-badge-font-size: var(--asc-font-size-text-sm);  /* 14px — matches Figma */
    --asc-badge-line-height: var(--asc-line-height-text-sm); /* 20px */
    --asc-badge-gap: var(--asc-spacing-xs);               /* 4px */
    --asc-badge-icon-size: var(--asc-spacing-lg);      /* 12px */
    --asc-badge-avatar-size: var(--asc-spacing-xl);      /* 16px */
  }

  :host([size='lg']) {
    --asc-badge-padding-x: var(--asc-spacing-lg);         /* 12px */
    --asc-badge-padding-y: var(--asc-spacing-xs);         /* 4px */
    --asc-badge-font-size: var(--asc-font-size-text-sm);  /* 14px */
    --asc-badge-line-height: var(--asc-line-height-text-sm); /* 20px */
    --asc-badge-gap: var(--asc-spacing-sm);               /* 6px */
    --asc-badge-icon-size: 14px; /* no-token: 14px not on spacing scale */
    --asc-badge-avatar-size: var(--asc-spacing-2xl);     /* 20px */
    --asc-badge-icon-only-size: 28px; /* no-token: 28px not on spacing scale */
  }

  /* ═══════════════════════════════════════════════════════════
     VARIANTS
     ═══════════════════════════════════════════════════════════ */
  :host([variant='pill']) {
    --asc-badge-border-radius: var(--asc-radius-full); /* 9999px — fully rounded capsule */
  }

  :host([variant='badge']) {
    --asc-badge-border-radius: var(--asc-radius-xs); /* 4px — lightly rounded rectangle */
  }

  /* ═══════════════════════════════════════════════════════════
     THEMES
     ═══════════════════════════════════════════════════════════ */
  :host([theme='gray']) {
    --asc-badge-bg: var(--asc-bg-primary);
    --asc-badge-border-color: var(--asc-border-primary);
    --asc-badge-fg: var(--asc-text-primary);           /* --colors/text/text-primary per Figma */
    --asc-badge-avatar-ring-color: var(--asc-border-primary);
  }

  :host([theme='brand']) {
    --asc-badge-bg: var(--asc-bg-brand-primary);
    --asc-badge-border-color: var(--asc-border-brand);
    --asc-badge-fg: var(--asc-text-brand-primary);     /* --colors/text/text-brand-primary per Figma */
    --asc-badge-icon-color: var(--asc-fg-brand-primary); /* --colors/foreground/fg-brand-primary per Figma */
    --asc-badge-avatar-ring-color: var(--asc-border-brand);
  }

  :host([theme='success']) {
    --asc-badge-bg: var(--asc-bg-success-primary);
    --asc-badge-border-color: var(--asc-border-success);
    --asc-badge-fg: var(--asc-text-brand-primary);     /* Figma binds success text to text-brand-primary, not text-success */
    --asc-badge-icon-color: var(--asc-fg-success-primary); /* --colors/foreground/fg-success-primary per Figma */
    --asc-badge-avatar-ring-color: var(--asc-border-success);
  }

  :host([theme='error']) {
    --asc-badge-bg: var(--asc-bg-error-primary);
    --asc-badge-border-color: var(--asc-border-error);
    --asc-badge-fg: var(--asc-text-error-primary);     /* --colors/text/text-error-primary per Figma */
    --asc-badge-icon-color: var(--asc-fg-error-primary); /* --colors/foreground/fg-error-primary per Figma */
    --asc-badge-avatar-ring-color: var(--asc-border-error);
  }

  /* ═══════════════════════════════════════════════════════════
     LAYOUT HELPERS
     ═══════════════════════════════════════════════════════════ */
  [hidden] {
    display: none !important;
  }

  /* ═══════════════════════════════════════════════════════════
     BASE ELEMENT
     ═══════════════════════════════════════════════════════════ */
  .base {
    display: inline-flex;
    align-items: center;
    gap: var(--asc-badge-gap);
    padding: var(--asc-badge-padding-y) var(--asc-badge-padding-x);
    background-color: var(--asc-badge-bg);
    border: var(--asc-badge-border-width) solid var(--asc-badge-border-color);
    border-radius: var(--asc-badge-border-radius);
    color: var(--asc-badge-fg);
    font-family: var(--asc-font-family-body);
    font-size: var(--asc-badge-font-size);
    font-weight: var(--asc-badge-font-weight);
    line-height: var(--asc-badge-line-height);
    white-space: nowrap;
    cursor: default;
    box-sizing: border-box;
    user-select: none;
  }

  /* ═══════════════════════════════════════════════════════════
     SLOT WRAPPERS
     ═══════════════════════════════════════════════════════════ */
  .leading,
  .trailing {
    display: inline-flex;
    align-items: center;
    flex-shrink: 0;
    /* Use the icon-specific foreground token so icons and dots get the
       correct per-theme color (--asc-fg-*), separate from the text color
       (--asc-badge-fg / --asc-text-*). Confirmed from Figma screenshot:
       success dot = fg-success-primary, brand dot = fg-brand-primary, etc. */
    color: var(--asc-badge-icon-color);
  }

  .label {
    display: inline-flex;
    align-items: center;
  }

  /* Auto-size SVG icons in leading/trailing slots to --asc-badge-icon-size */
  .leading ::slotted(svg),
  .trailing ::slotted(svg) {
    width: var(--asc-badge-icon-size);
    height: var(--asc-badge-icon-size);
    flex-shrink: 0;
    display: block;
  }

  /*
   * Pass --asc-badge-avatar-ring-color through to asc-avatar via a shared
   * custom property. The avatar component is expected to read
   * --asc-avatar-ring-color from its inherited styles.
   */
  .leading {
    --asc-avatar-ring-color: var(--asc-badge-avatar-ring-color);
  }

  /* ═══════════════════════════════════════════════════════════
     ICON-ONLY
     ═══════════════════════════════════════════════════════════ */
  :host([icon-only]) .base {
    width: var(--asc-badge-icon-only-size);
    height: var(--asc-badge-icon-only-size);
    padding: 0;
    justify-content: center;
  }

  /* ═══════════════════════════════════════════════════════════
     SCREEN-READER ONLY (icon-only mode)
     Position: absolute so it does not affect badge layout.
     Not display:none — screen readers must still traverse it.
     ═══════════════════════════════════════════════════════════ */
  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border-width: 0;
  }
`;
